import { Box, IconButton } from '@mui/material'
import React, { ReactElement, useCallback } from 'react'
import { Criteria_Group_Sorting_Config, Criteria_Sorting_Config } from 'src/@types/graphql'
import { EditIcon, MoveDownIcon, MoveUpIcon } from 'src/shared/icons/Icons'
import { HtmlRenderer } from 'src/shared/presentation/HtmlRenderer'
import { S } from 'src/shared/styled/S'
import { useUserLocale } from 'src/user/UserContext'
import { ROUTES } from 'src/routing/routes'
import { PROCESS_TYPE } from 'src/shared/constants/constants'
import { useDelayedNavigate } from 'src/shared/utils/hooks/navigation-hooks'

type Props = {
  criteriaGroupsSorting: Criteria_Group_Sorting_Config[]
  onSwitchCriteriaGroupPosition: (
    cg1: Criteria_Group_Sorting_Config,
    cg2: Criteria_Group_Sorting_Config,
  ) => Promise<void>
  onSwitchCriteriaPosition: (c1: Criteria_Sorting_Config, c2: Criteria_Sorting_Config) => Promise<void>
  isReorderingLocked: boolean
  processType: PROCESS_TYPE
}
export const CriteriaGroupAndCriteriaSortList = ({
  criteriaGroupsSorting,
  onSwitchCriteriaGroupPosition,
  onSwitchCriteriaPosition,
  isReorderingLocked = false,
  processType,
}: Props): ReactElement => {
  const locale = useUserLocale()
  const navigate = useDelayedNavigate()

  // handlers
  const getCriteriaConfigSortings = (criteriaGroupSorting: Criteria_Group_Sorting_Config) => {
    return criteriaGroupSorting.criteria_group_config.criteria_sorting_configs
  }

  const onEditCriteriaGroupClick = useCallback(
    (e: React.MouseEvent<HTMLButtonElement>, groupId: number) => {
      e.stopPropagation()
      navigate(
        ROUTES.CriteriaConfigurationIndex.nested.CriteriaConfigurationCriteriaGroupsEdit.params({
          groupId,
        }),
        { state: { processType: processType } },
      )
    },
    [navigate, processType],
  )

  const onEditCriteriaClick = useCallback(
    (e: React.MouseEvent<HTMLButtonElement>, criteriaId: number) => {
      e.stopPropagation()
      navigate(
        ROUTES.CriteriaConfigurationIndex.nested.CriteriaConfigurationEvaluationCriteriaEdit.params({
          criteriaId,
        }),
        { state: { processType: processType } },
      )
    },
    [navigate, processType],
  )

  // JSX
  const renderGroupHeader = (
    cgs: Criteria_Group_Sorting_Config,
    cgIndex: number,
    criteriaGroupsSorting: Criteria_Group_Sorting_Config[],
    locale: string,
    isReorderingLocked: boolean,
    onSwitchCriteriaGroupPosition: (a: Criteria_Group_Sorting_Config, b: Criteria_Group_Sorting_Config) => void,
  ) => (
    <S.ConfigurationSortCard.Card greyedOut={!cgs.criteria_group_config.group_active} transparent={true}>
      <Box className="header-wrapper">
        <Box className="header-content">
          <S.ConfigurationSortCard.Header
            avatar={
              <S.ConfigurationSortCard.Box>
                <S.ConfigurationSortCard.MoveButtons>
                  <IconButton
                    color="inherit"
                    disabled={cgIndex === 0 || isReorderingLocked}
                    onClick={() =>
                      onSwitchCriteriaGroupPosition(criteriaGroupsSorting[cgIndex], criteriaGroupsSorting[cgIndex - 1])
                    }
                    size="small"
                  >
                    <MoveUpIcon fontSize="small" />
                  </IconButton>
                  <IconButton
                    color="inherit"
                    disabled={cgIndex === criteriaGroupsSorting.length - 1 || isReorderingLocked}
                    onClick={() =>
                      onSwitchCriteriaGroupPosition(criteriaGroupsSorting[cgIndex], criteriaGroupsSorting[cgIndex + 1])
                    }
                    size="small"
                  >
                    <MoveDownIcon fontSize="small" />
                  </IconButton>
                </S.ConfigurationSortCard.MoveButtons>
              </S.ConfigurationSortCard.Box>
            }
            title={
              <S.Box.TruncateBox>
                <HtmlRenderer variant="h2" html={cgs.criteria_group_config.names[locale]} color={'textPrimary'} />
              </S.Box.TruncateBox>
            }
            action={
              <IconButton
                sx={{ m: 0, mr: -2 }}
                onClick={(e) => onEditCriteriaGroupClick(e, cgs.group_id)}
                color="primary"
                size={'small'}
              >
                <EditIcon fontSize={'small'} />
              </IconButton>
            }
            disableTypography
          />
        </Box>
      </Box>
    </S.ConfigurationSortCard.Card>
  )

  const renderCriteriaItem = (
    cs: Criteria_Sorting_Config,
    cIndex: number,
    cgs: Criteria_Group_Sorting_Config,
    locale: string,
    isReorderingLocked: boolean,
    onSwitchCriteriaPosition: (a: Criteria_Sorting_Config, b: Criteria_Sorting_Config) => void,
  ) => (
    <Box mt={1} key={cs.id}>
      <S.ConfigurationSortCard.Card greyedOut={!cs.criteria_config.active}>
        <Box className="header-wrapper">
          <Box className="header-content">
            <S.ConfigurationSortCard.Header
              avatar={
                <S.ConfigurationSortCard.Box>
                  <S.ConfigurationSortCard.IdChip size="small" label={cs.criteria_config.criteria_id} />
                  <S.ConfigurationSortCard.MoveButtons>
                    <IconButton
                      color="inherit"
                      disabled={cIndex === 0 || isReorderingLocked}
                      onClick={() =>
                        onSwitchCriteriaPosition(
                          getCriteriaConfigSortings(cgs)[cIndex],
                          getCriteriaConfigSortings(cgs)[cIndex - 1],
                        )
                      }
                      size="large"
                    >
                      <MoveUpIcon />
                    </IconButton>
                    <IconButton
                      color="inherit"
                      disabled={
                        cIndex === cgs.criteria_group_config.criteria_sorting_configs.length - 1 || isReorderingLocked
                      }
                      onClick={() =>
                        onSwitchCriteriaPosition(
                          getCriteriaConfigSortings(cgs)[cIndex],
                          getCriteriaConfigSortings(cgs)[cIndex + 1],
                        )
                      }
                      size="large"
                    >
                      <MoveDownIcon />
                    </IconButton>
                  </S.ConfigurationSortCard.MoveButtons>
                </S.ConfigurationSortCard.Box>
              }
              title={
                <S.Box.TruncateBox>
                  <HtmlRenderer
                    variant="subtitle2"
                    html={cs.criteria_config.description[locale]}
                    color={'textPrimary'}
                  />
                </S.Box.TruncateBox>
              }
              action={
                <IconButton
                  sx={{ m: 0, mr: -2 }}
                  onClick={(e) => onEditCriteriaClick(e, cs.criteria_id)}
                  color="primary"
                  size={'small'}
                >
                  <EditIcon fontSize={'small'} />
                </IconButton>
              }
              disableTypography
            />
          </Box>
        </Box>
      </S.ConfigurationSortCard.Card>
    </Box>
  )

  return (
    <>
      {criteriaGroupsSorting.map((cgs, cgIndex) => (
        <Box key={cgs.id}>
          {/* Render criteria groups */}
          {renderGroupHeader(
            cgs,
            cgIndex,
            criteriaGroupsSorting,
            locale,
            isReorderingLocked,
            onSwitchCriteriaGroupPosition,
          )}

          {/* Render criteria in group */}
          <Box pl={2}>
            {getCriteriaConfigSortings(cgs).map((cs, cIndex) =>
              renderCriteriaItem(cs, cIndex, cgs, locale, isReorderingLocked, onSwitchCriteriaPosition),
            )}
          </Box>
        </Box>
      ))}
    </>
  )
}
